import get from 'lodash/get';
import React, { createContext, PropsWithChildren, useContext, useEffect } from 'react';
import { FieldValues, FormProvider, SubmitErrorHandler } from 'react-hook-form';
import styled from 'styled-components';
import { AnyObjectSchema } from 'yup';

import { useFormWithYup } from 'utils/hooks';

import { Spinner } from '../Spinner';

export interface FormProps extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onInvalid'> {
    formSchema?: AnyObjectSchema;
    onSubmit?: (data: any) => void;
    submitOnChange?: boolean;
    onInvalid?: SubmitErrorHandler<FieldValues>;
    defaultValues?: FieldValues;
    disabled?: boolean;
    isLoading?: boolean;
    labelWidth?: string;
}

const FormMetaContext = createContext<{
    fields?: any;
    labelWidth?: string;
}>({});

export const Form = ({
    formSchema,
    onSubmit,
    children,
    submitOnChange,
    onInvalid,
    defaultValues,
    disabled,
    isLoading,
    labelWidth,
    ...otherFormProps
}: PropsWithChildren<FormProps>) => {
    const formMethods = useFormWithYup(formSchema, { abortEarly: false }, { defaultValues });
    const formOnSubmit = onSubmit ? formMethods.handleSubmit(onSubmit, onInvalid) : undefined;

    useEffect(() => {
        if (defaultValues) {
            formMethods.reset(defaultValues);
        }
    }, [defaultValues, formMethods]);

    useEffect(() => {
        if (!submitOnChange || !onSubmit) {
            return;
        }
        const subscription = formMethods.watch(() => formMethods.handleSubmit(onSubmit)());
        return () => subscription.unsubscribe();
    }, [formMethods, formMethods.watch, onSubmit, submitOnChange]);

    return (
        <FormProvider {...formMethods}>
            <FormMetaContext.Provider
                value={{
                    fields: formSchema?.fields,
                    labelWidth,
                }}
            >
                <StyledForm
                    onSubmit={(ev) => {
                        ev.preventDefault();
                        formOnSubmit?.();
                        ev.stopPropagation();
                    }}
                    {...otherFormProps}
                >
                    {isLoading && <LoadingOverlay />}
                    <StyledFieldset disabled={disabled || isLoading}>{children}</StyledFieldset>
                </StyledForm>
            </FormMetaContext.Provider>
        </FormProvider>
    );
};

const StyledForm = styled.form`
    position: relative;
    width: 100%;
`;

const LoadingOverlay = () => (
    <Overlay>
        <Spinner size="large" />
    </Overlay>
);

const Overlay = styled.div`
    background: #0000000f;
    z-index: 10;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const StyledFieldset = styled.fieldset`
    display: contents;
`;

export const useFieldValidation = (name: string) => {
    const ctx = useContext(FormMetaContext);
    const fieldName = name.replace(/\./g, '.fields.');
    return {
        isRequired: get(ctx.fields, fieldName)?.exclusiveTests?.required ?? false,
    };
};

export const useFormLabelWidth = () => {
    const ctx = useContext(FormMetaContext);
    return ctx.labelWidth;
};
